'use client';

import { useTranslations } from 'next-intl';
import { useState, useEffect } from 'react';
import { VariantProps } from 'tailwind-variants';

import { UiKitSelectBase } from '@/fo/ui/kit/select/base';
import { MappedProduct } from '@/fo/util/map-product-data';

import { styles } from './UiProductOptionsBase.styles';

type Variants = VariantProps<typeof styles>;

interface SelectOption {
  label: string;
  value: string;
  stockStatus?: string | null;
  product?: MappedProduct;
}

interface HeightOption {
  valueIndex: number;
  label: string;
  stockStatus: string | null;
  isSelected: boolean;
  sizeOptions: Array<{
    valueIndex: number;
    label: string;
    stockStatus: string | null;
    isSelected: boolean;
    product?: MappedProduct;
  }>;
}

interface SizeOnlyOption {
  valueIndex: number;
  label: string;
  stockStatus: string | null;
  isSelected: boolean;
  product?: MappedProduct;
}

export interface UiProductOptionsGroupedProps {
  className?: string;
  options?: HeightOption[] | SizeOnlyOption[];
  size: Variants['size'];
  onOptionChange?: (product: MappedProduct) => void;
}

export function UiProductOptionsGrouped({
  className,
  options,
  onOptionChange
}: UiProductOptionsGroupedProps) {
  const t = useTranslations();
  const [selectedHeight, setSelectedHeight] = useState<string | undefined>();
  const [selectedSize, setSelectedSize] = useState<string | undefined>();

  // Сбрасываем размер при изменении высоты
  useEffect(() => {
    setSelectedSize(undefined);
  }, [selectedHeight]);

  if (!options || options.length === 0) {
    return null;
  }

  // Проверяем, это HeightOption[] или SizeOnlyOption[]
  const isHeightOptions = 'sizeOptions' in options[0];

  if (isHeightOptions) {
    // Логика для HeightOption[] (height + size)
    const heightOptions: SelectOption[] = options.map((option) => ({
      label: option.label,
      value: option.valueIndex.toString(),
      stockStatus: option.stockStatus
    }));

    // Преобразуем опции size в формат для UiKitSelectBase
    const sizeOptions: SelectOption[] = selectedHeight
      ? (options as HeightOption[])
          .find((option) => option.valueIndex.toString() === selectedHeight)
          ?.sizeOptions.map((sizeOption) => ({
            label: sizeOption.label,
            value: sizeOption.valueIndex.toString(),
            stockStatus: sizeOption.stockStatus,
            product: sizeOption.product
          })) || []
      : [];

    const handleHeightChange = (option: SelectOption | null) => {
      if (!option) {
        setSelectedHeight(undefined);
        setSelectedSize(undefined);
        return;
      }

      setSelectedHeight(option.value);
      setSelectedSize(undefined); // Сбрасываем выбор размера при смене высоты
    };

    const handleSizeChange = (option: SelectOption | null) => {
      if (!option) {
        setSelectedSize(undefined);
        return;
      }

      setSelectedSize(option.value);

      if (option.product) {
        onOptionChange?.(option.product);
      }
    };

    const selectedHeightOption = heightOptions.find(
      (option) => option.value === selectedHeight
    );
    const selectedSizeOption = sizeOptions.find(
      (option) => option.value === selectedSize
    );

    return (
      <div className={className}>
        <div className="mb-4">
          <UiKitSelectBase
            label={t('form.label.height')}
            options={heightOptions}
            selected={selectedHeightOption || null}
            onChange={handleHeightChange}
            placeholder={t('form.placeholder.height')}
            disabled={heightOptions.length === 0}
          />
        </div>

        <div className="mb-4">
          <UiKitSelectBase
            label={t('form.label.size')}
            options={sizeOptions}
            selected={selectedSizeOption || null}
            onChange={handleSizeChange}
            placeholder={t('form.placeholder.size')}
            disabled={!selectedHeight || sizeOptions.length === 0}
          />
        </div>

        {/* Показываем выбранные варианты */}
        {(selectedHeightOption || selectedSizeOption) && (
          <div className="mt-2 text-sm text-gray-600">
            {selectedHeightOption &&
              `${t('form.label.height')}: ${selectedHeightOption.label}`}
            {selectedHeightOption && selectedSizeOption && ', '}
            {selectedSizeOption &&
              `${t('form.label.size')}: ${selectedSizeOption.label}`}
          </div>
        )}
      </div>
    );
  } else {
    // Логика для SizeOnlyOption[] (только size)
    const sizeOptions: SelectOption[] = (options as SizeOnlyOption[]).map(
      (option) => ({
        label: option.label,
        value: option.valueIndex.toString(),
        stockStatus: option.stockStatus,
        product: option.product
      })
    );

    const handleSizeChange = (option: SelectOption | null) => {
      if (!option) {
        setSelectedSize(undefined);
        return;
      }

      setSelectedSize(option.value);

      if (option.product) {
        onOptionChange?.(option.product);
      }
    };

    const selectedSizeOption = sizeOptions.find(
      (option) => option.value === selectedSize
    );

    return (
      <div className={className}>
        <div className="mb-4">
          <UiKitSelectBase
            label={t('form.label.size')}
            options={sizeOptions}
            selected={selectedSizeOption || null}
            onChange={handleSizeChange}
            placeholder={t('form.placeholder.size')}
            disabled={sizeOptions.length === 0}
          />
        </div>

        {/* Показываем выбранные варианты */}
        {selectedSizeOption && (
          <div className="mt-2 text-sm text-gray-600">
            {t('form.label.size')}: {selectedSizeOption.label}
          </div>
        )}
      </div>
    );
  }
}
